import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from "axios";
import { 
  Box, Typography, Paper, TextField, Button, Alert, CircularProgress, Grid, MenuItem, FormControl, InputLabel, Select,
  Snackbar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { API_ENDPOINTS } from '../../../../config/api';

const AddUser = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(''); // Separate state for role
  const [permissions, setPermissions] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
    mobile: '',
    address: ''
    // Removed role from formData
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { state: { from: '/users/create' } });
      return;
    }
    fetchRoles();
  }, [navigate]);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(API_ENDPOINTS.ROLES.BASE, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setRoles(response.data.data || []);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to fetch roles');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRoleChange = (e) => {
    setSelectedRole(e.target.value);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      if (!token) {
        navigate('/login', { state: { from: '/users/create' } });
        return;
      }

      if (!formData.name || !formData.email || !formData.password || !selectedRole) {
        setSnackbar({
          open: true,
          message: 'Name, Email, Password, and Role are required',
          severity: 'error'
        });
        setLoading(false);
        return;
      }

      if (formData.password !== formData.password_confirmation) {
        setSnackbar({
          open: true,
          message: 'Passwords do not match',
          severity: 'error'
        });
        setLoading(false);
        return;
      }

      // Create user data with role
      const userData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        mobile: formData.mobile,
        address: formData.address,
        role: selectedRole // Include role in userData
      };

      // Step 1: Create the user
      const response = await axios.post(API_ENDPOINTS.USERS.BASE, userData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const userId = response.data.data.id;

        // Step 2: Assign role separately (if needed)
        await axios.post(`${API_ENDPOINTS.USERS.BASE}/assign-role`, {
          user_id: userId,
          role: selectedRole
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Fetch permissions (optional)
        const permResponse = await axios.get(`${API_ENDPOINTS.USERS.BASE}/${userId}/permissions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPermissions(permResponse.data.data);

        setSnackbar({
          open: true,
          message: 'User created successfully!',
          severity: 'success'
        });

        // Navigate after a short delay to show the success message
        setTimeout(() => {
          navigate('/users');
        }, 1500);
      }
    } catch (error) {
      
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to create user or assign role',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            {/* <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/users')} sx={{ mr: 2 }}>
              Back to Users
            </Button>
            <Typography variant="h5">
              Create New User
            </Typography> */}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="div" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Name" name="name" value={formData.name} onChange={handleChange} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email" name="email" type="email" value={formData.email} onChange={handleChange} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Mobile" name="mobile" value={formData.mobile} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Address" name="address" value={formData.address} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Password" name="password" type="password" value={formData.password} onChange={handleChange} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Confirm Password" name="password_confirmation" type="password" value={formData.password_confirmation} onChange={handleChange} required />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select name="role" value={selectedRole} 
                  onChange={handleRoleChange} 
                  labelId="user-role-select"
                    id="user-role-select"
                    label="Role"
                  required>
                    <MenuItem value="">Select Role</MenuItem>
                    {roles.map((role) => (
                      <MenuItem key={role.id} value={role.name}>
                        {role.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button variant="contained" color="primary" onClick={handleSubmit} disabled={loading}>
                {loading ? <CircularProgress size={24} /> : 'Create User'}
              </Button>
              <Button variant="outlined" onClick={() => navigate('/users')} disabled={loading}>
                Cancel
              </Button>
            </Box>
          </Box>
        </Paper>
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default AddUser;